import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

/* ============================================================
   NOTIFICAÇÕES — contagem partilhada

   Só conta o que é iminente: os contratos cujo prazo para comunicar a não
   renovação está à vista ou já passou — os mesmos que piscam no ecrã. É esse o
   número vermelho da barra lateral. O resto das notificações não entra: um
   número que some tudo deixa de querer dizer "trata disto agora".

   Segue o padrão do contexto dos registos tardios: sem polling, recarrega
   quando o separador volta a ficar visível ou a janela ganha foco.
   ============================================================ */

const NotificacoesContext = createContext({
    urgentes: 0, carregando: false, recarregar: () => {},
});

export const useNotificacoes = () => useContext(NotificacoesContext);

export const NotificacoesProvider = ({ children }) => {
    const { isAuthenticated, loading: authLoading, hasPermission } = useAuth();
    const [urgentes, setUrgentes] = useState(0);
    const [carregando, setCarregando] = useState(false);

    // Sem isto, as rotas públicas (onboarding, consulta por token) fariam este
    // pedido sem sessão: o interceptor trata qualquer 401 como sessão expirada
    // e atira o visitante para o login.
    const podeConsultar = !authLoading && isAuthenticated
        && (typeof hasPermission !== 'function' || hasPermission('notificacoes'));

    const buscar = useCallback(async (silencioso = false) => {
        if (!podeConsultar) return;
        if (!silencioso) setCarregando(true);
        try {
            const r = await api.get('/notificacoes');
            setUrgentes(r.data?.urgentes || 0);
        } catch {
            // indicador acessório: nunca deve interromper o que se está a fazer
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [podeConsultar]);

    useEffect(() => {
        if (!podeConsultar) { setUrgentes(0); return; }
        buscar();
        const aoVoltar = () => { if (document.visibilityState === 'visible') buscar(true); };
        document.addEventListener('visibilitychange', aoVoltar);
        window.addEventListener('focus', aoVoltar);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltar);
            window.removeEventListener('focus', aoVoltar);
        };
    }, [podeConsultar, buscar]);

    return (
        <NotificacoesContext.Provider value={{ urgentes, carregando, recarregar: buscar }}>
            {children}
        </NotificacoesContext.Provider>
    );
};

export default NotificacoesContext;
